import type { ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import {
  type RoutePermissionInput,
  type RoutePermissionMode,
  useRoutePermission,
} from '@/hooks/navigation/useRoutePermission';
import { cn } from '@/lib/utils';

interface RoutePermissionDeniedProps {
  title?: string;
  message: string;
  action?: ReactNode;
  className?: string;
}

interface RoutePermissionGuardProps extends RoutePermissionDeniedProps {
  permissions: RoutePermissionInput;
  mode?: RoutePermissionMode;
  children: ReactNode;
}

export function RoutePermissionDenied({
  title = 'Permission required',
  message,
  action,
  className,
}: RoutePermissionDeniedProps) {
  return (
    <div
      className={cn(
        'flex h-full min-h-[12rem] flex-col items-center justify-center gap-3 p-6 text-center',
        className,
      )}
    >
      <AlertCircle className="h-10 w-10 text-muted-foreground" />
      <div className="space-y-1">
        <p className="font-semibold">{title}</p>
        <p className="text-sm text-muted-foreground">{message}</p>
      </div>
      {action}
    </div>
  );
}

export function RoutePermissionGuard({
  permissions,
  mode = 'all',
  title,
  message,
  action,
  className,
  children,
}: RoutePermissionGuardProps) {
  const canAccess = useRoutePermission(permissions, mode);

  if (!canAccess) {
    return (
      <RoutePermissionDenied
        title={title}
        message={message}
        action={action}
        className={className}
      />
    );
  }

  return <>{children}</>;
}
